/**
 * Which block are you looking at?
 *
 * A voxel ray-march — Amanatides & Woo's algorithm — walking cell by cell from
 * the eye along the view direction until it meets something solid. It is the
 * one piece of geometry that place and break both need, and it is deliberately
 * in `shared` rather than in the client for a reason that matters:
 *
 * ⚠️ **The server re-runs it.** A client packet says "I broke the block at
 * 347580, 12, -396315", and the server has to decide whether that block was
 * reachable from where the player actually was. Sharing the code means the two
 * answers cannot drift apart (`Development/CLAUDE.md` #6).
 *
 * Pure arithmetic and a callback, so it tests without a browser, a GPU or a
 * database — which is most of why the check is affordable to run twice.
 */

import type { WorldPosition } from './coords.ts';

/**
 * How far you can reach, in metres.
 *
 * ⚠️ **A rule, not a preference.** It is what stops a modified client editing
 * the far side of the world, so the number lives here, next to the code that
 * enforces it, rather than in the client where it would be a suggestion.
 *
 * 6 m is a little longer than Minecraft's ~4.5, because a block here is a real
 * metre and 4.5 m is a shorter reach than it sounds when everything around you
 * is life-sized.
 */
export const MAX_REACH = 6;

export interface VoxelHit {
  /** The solid block the ray met. */
  readonly block: WorldPosition;
  /**
   * The empty cell in front of it — where a placed block goes.
   *
   * This is the face the ray entered through, which is what makes placing feel
   * right: you build onto the side you are looking at, not into the block.
   */
  readonly place: WorldPosition;
  /** How far the eye was from the face, in metres. */
  readonly distance: number;
}

/** Is there something solid at this integer block coordinate? */
export interface SolidAt {
  (x: number, y: number, z: number): boolean;
}

/**
 * March a ray through the voxel grid and return the first solid block.
 *
 * @param origin where the ray starts — the eye, in world metres
 * @param direction which way it points. Need not be normalised.
 * @param maxDistance how far to look, in metres
 * @param solidAt what the world contains
 *
 * Returns `null` when nothing solid is within reach, which is the ordinary case
 * when looking at the sky.
 */
export function raycastVoxels(
  origin: WorldPosition,
  direction: WorldPosition,
  maxDistance: number,
  solidAt: SolidAt,
): VoxelHit | null {
  const length = Math.hypot(direction.x, direction.y, direction.z);
  /* A zero-length direction has no ray to march. Return nothing rather than
     dividing by zero and marching a grid full of NaN, which silently reports a
     hit at coordinates no test would think to check. */
  if (!Number.isFinite(length) || length === 0) return null;

  const dx = direction.x / length;
  const dy = direction.y / length;
  const dz = direction.z / length;

  let x = Math.floor(origin.x);
  let y = Math.floor(origin.y);
  let z = Math.floor(origin.z);

  /* Standing inside solid ground: the eye is in a block. There is no face to
     have entered through, so there is nowhere to place — report the hit with
     `place` equal to the block itself and let the caller reject it. Silently
     returning null here would make breaking out of a hole impossible. */
  if (solidAt(x, y, z)) {
    return { block: { x, y, z }, place: { x, y, z }, distance: 0 };
  }

  const stepX = Math.sign(dx);
  const stepY = Math.sign(dy);
  const stepZ = Math.sign(dz);

  /* How far along the ray one whole cell of movement costs, per axis. A ray
     parallel to an axis never crosses that axis's boundaries — Infinity is the
     honest answer and it drops that axis out of the comparison below. */
  const tDeltaX = dx === 0 ? Infinity : Math.abs(1 / dx);
  const tDeltaY = dy === 0 ? Infinity : Math.abs(1 / dy);
  const tDeltaZ = dz === 0 ? Infinity : Math.abs(1 / dz);

  const firstBoundary = (start: number, cell: number, d: number, step: number): number => {
    if (d === 0) return Infinity;
    return step > 0 ? (cell + 1 - start) / d : (cell - start) / d;
  };

  let tMaxX = firstBoundary(origin.x, x, dx, stepX);
  let tMaxY = firstBoundary(origin.y, y, dy, stepY);
  let tMaxZ = firstBoundary(origin.z, z, dz, stepZ);

  /* The face last crossed, as a normal pointing back the way we came. */
  let nx = 0;
  let ny = 0;
  let nz = 0;

  for (;;) {
    /* Step across whichever axis boundary is nearest. */
    let t: number;
    if (tMaxX <= tMaxY && tMaxX <= tMaxZ) {
      t = tMaxX;
      x += stepX;
      tMaxX += tDeltaX;
      nx = -stepX;
      ny = 0;
      nz = 0;
    } else if (tMaxY <= tMaxZ) {
      t = tMaxY;
      y += stepY;
      tMaxY += tDeltaY;
      nx = 0;
      ny = -stepY;
      nz = 0;
    } else {
      t = tMaxZ;
      z += stepZ;
      tMaxZ += tDeltaZ;
      nx = 0;
      ny = 0;
      nz = -stepZ;
    }

    if (t > maxDistance) return null;

    if (solidAt(x, y, z)) {
      return {
        block: { x, y, z },
        place: { x: x + nx, y: y + ny, z: z + nz },
        distance: t,
      };
    }
  }
}
